import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface TripStep {
  id: string;
  city: string;
  duration_days: number;
  order_index: number;
  notes: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface TripRoute {
  id: string;
  from_step_id: string;
  to_step_id: string;
  transport_type: string | null;
  notes: string | null;
  departure_time: string | null;
  travel_duration_minutes: number | null;
}

interface Trip {
  id: string;
  title: string;
  slug: string;
  countries: string[];
  start_date: string | null;
  end_date: string | null;
  user_id: string;
  cover_image: string | null;
  budget: number | null;
  budget_public: boolean;
  is_public: boolean;
  trip_type: "trip" | "vacation";
  characteristics: string[];
}

interface StepFeedback {
  id: string;
  step_id: string;
  rating: string;
  comment: string | null;
}

interface StepCounts {
  accommodations: number;
  restaurants: number;
  activities: number;
}

interface TripData {
  trip: Trip;
  steps: TripStep[];
  routes: TripRoute[];
  feedbacks: Record<string, { id: string; rating: string; comment: string | null; photos: string[] | null }>;
  stepCounts: Record<string, StepCounts>;
  isCollaborator: boolean;
}

// Helper to check if a string is a UUID
const isUUID = (str: string) => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Fetch all trip data in parallel with optimized queries
 */
const fetchTripData = async (
  slugOrId: string,
  userEmail: string | null | undefined
): Promise<TripData | null> => {
  // First, fetch the trip by slug or id
  let tripQuery;
  if (isUUID(slugOrId)) {
    tripQuery = supabase.from("trips").select("*").eq("id", slugOrId).maybeSingle();
  } else {
    tripQuery = supabase.from("trips").select("*").eq("slug", slugOrId).maybeSingle();
  }

  const { data: tripData, error: tripError } = await tripQuery;

  if (tripError || !tripData) {
    return null;
  }

  // Load all data in parallel including collaborator check
  const [
    { data: stepsData },
    { data: routesData },
    { data: feedbacksData },
    { data: accommodationsData },
    { data: restaurantsData },
    { data: activitiesData },
    collabResult
  ] = await Promise.all([
    supabase.from("trip_steps").select("*").eq("trip_id", tripData.id).order("order_index", { ascending: true }),
    supabase.from("trip_routes").select("*").eq("trip_id", tripData.id),
    supabase.from("step_feedbacks").select("id, step_id, rating, comment").eq("trip_id", tripData.id),
    supabase.from("accommodations").select("step_id").eq("trip_id", tripData.id),
    supabase.from("restaurants").select("step_id").eq("trip_id", tripData.id),
    supabase.from("activities").select("step_id").eq("trip_id", tripData.id),
    userEmail
      ? supabase.from("trip_collaborators").select("id").eq("trip_id", tripData.id).eq("email", userEmail).maybeSingle()
      : Promise.resolve({ data: null })
  ]);

  // Calculate step counts from fetched data
  const stepCounts: Record<string, StepCounts> = {};
  (stepsData || []).forEach((step) => {
    stepCounts[step.id] = {
      accommodations: (accommodationsData || []).filter(a => a.step_id === step.id).length,
      restaurants: (restaurantsData || []).filter(r => r.step_id === step.id).length,
      activities: (activitiesData || []).filter(a => a.step_id === step.id).length,
    };
  });

  // Build feedbacks map
  const feedbacksMap: Record<string, { id: string; rating: string; comment: string | null; photos: string[] | null }> = {};
  (feedbacksData || []).forEach((feedback) => {
    feedbacksMap[feedback.step_id] = {
      id: feedback.id,
      rating: feedback.rating,
      comment: feedback.comment,
      photos: null, // Photos are in trip_photos
    };
  });

  return {
    trip: tripData,
    steps: stepsData || [],
    routes: routesData || [],
    feedbacks: feedbacksMap,
    stepCounts,
    isCollaborator: !!collabResult?.data,
  };
};

/**
 * React Query hook for trip data with caching
 */
export const useTripData = (slugOrId: string | undefined, userEmail: string | null | undefined) => {
  return useQuery({
    queryKey: ["trip", slugOrId, userEmail],
    queryFn: () => fetchTripData(slugOrId!, userEmail),
    enabled: !!slugOrId,
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Cache kept for 30 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
};

/**
 * Prefetch trip data into React Query cache
 */
export const useTripPrefetch = () => {
  const queryClient = useQueryClient();

  const prefetch = async (tripId: string) => {
    // Only prefetch if not already in cache
    const existingData = queryClient.getQueryData(["trip", tripId]);
    if (existingData) return;

    await queryClient.prefetchQuery({
      queryKey: ["trip", tripId, null],
      queryFn: () => fetchTripData(tripId, null),
      staleTime: 5 * 60 * 1000,
    });
  };

  return { prefetch };
};

/**
 * Invalidate trip cache after mutations
 */
export const useInvalidateTripCache = () => {
  const queryClient = useQueryClient();

  const invalidate = (tripId: string) => {
    queryClient.invalidateQueries({ queryKey: ["trip"], predicate: (query) => {
      const key = query.queryKey as string[];
      return key[1] === tripId || key[1]?.includes?.(tripId);
    }});
  };

  const invalidateStepCounts = async (tripId: string) => {
    // Refetch step counts data
    const [
      { data: accommodationsData },
      { data: restaurantsData },
      { data: activitiesData }
    ] = await Promise.all([
      supabase.from("accommodations").select("step_id").eq("trip_id", tripId),
      supabase.from("restaurants").select("step_id").eq("trip_id", tripId),
      supabase.from("activities").select("step_id").eq("trip_id", tripId),
    ]);

    // Update cache with new counts
    queryClient.setQueriesData({ queryKey: ["trip"] }, (oldData: TripData | undefined) => {
      if (!oldData || oldData.trip.id !== tripId) return oldData;
      
      const stepCounts: Record<string, StepCounts> = {};
      oldData.steps.forEach((step) => {
        stepCounts[step.id] = {
          accommodations: (accommodationsData || []).filter(a => a.step_id === step.id).length,
          restaurants: (restaurantsData || []).filter(r => r.step_id === step.id).length,
          activities: (activitiesData || []).filter(a => a.step_id === step.id).length,
        };
      });

      return { ...oldData, stepCounts };
    });
  };

  return { invalidate, invalidateStepCounts };
};

export type { Trip, TripStep, TripRoute, TripData, StepCounts };
